

import { decodeEntitiesDeep } from '@theme/utilities';

/**
 * Shipping countdown block.
 *
 * Counts down to the dispatch cut-off in UK time (Europe/London), whatever
 * timezone the visitor is in. Intl handles GMT/BST automatically, and the
 * remaining time is recomputed from the clock on every tick, so it stays
 * correct across DST changes, sleeping laptops and throttled tabs.
 *
 * States:
 *   - Mon-Fri before the cut-off  -> "Order in the next {time} ... ship today"
 *   - dispatch is next calendar day -> "ships tomorrow"
 *   - otherwise (Fri evening / Sat) -> "ships on Monday" (localised weekday)
 *   - after cut-off with the setting off -> hidden (still shown in the
 *     theme editor so the block remains selectable)
 */

const DAY_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// 2024-01-01 was a Monday; used only to obtain localised weekday names.
const WEEKDAY_REFERENCE = Date.UTC(2024, 0, 1, 12);
const MS_PER_DAY = 24 * 60 * 60 * 1000;


class ShippingCountdown extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[data-config]');
    this.messageEl = this.querySelector('[data-message]');
    if (!configEl || !this.messageEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);

      // `t` escapes these; they are written with textContent. See @theme/utilities.
      decodeEntitiesDeep(this.config.strings);
    } catch (error) {
      return;
    }

    this.londonFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    this.state = null;
    this.timeEl = null;
    this.tick = this.tick.bind(this);
    this.tick();
    this.timer = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Current wall-clock time in London: weekday index (0 = Mon) + seconds since midnight. */
  londonNow() {
    const parts = this.londonFormatter.formatToParts(new Date());
    const get = (type) => {
      const part = parts.find((p) => p.type === type);
      return part ? part.value : '';
    };
    return {
      day: DAY_KEYS.indexOf(get('weekday').slice(0, 3)),
      seconds:
        (parseInt(get('hour'), 10) % 24) * 3600 +
        parseInt(get('minute'), 10) * 60 +
        parseInt(get('second'), 10),
    };
  }

  tick() {
    const { day, seconds } = this.londonNow();
    if (day < 0 || Number.isNaN(seconds)) return;

    const cutoff = this.config.cutoffHour * 3600 + this.config.cutoffMinute * 60;
    const isWorkday = day <= 4;

    if (isWorkday && seconds < cutoff) {
      this.renderCountdown(cutoff - seconds);
    } else {
      this.renderAfterCutoff(day);
    }
  }

  renderCountdown(remaining) {
    if (this.state !== 'today') {
      this.state = 'today';
      // Build "Order in the next <strong/> for ..." around the [[time]]
      // placeholder with DOM nodes, so translations are never parsed as HTML.
      const [before = '', after = ''] = this.config.strings.shipsToday.split('[[time]]');
      const strong = document.createElement('strong');
      strong.className = 'shipping-countdown__time';
      this.messageEl.replaceChildren(
        document.createTextNode(before),
        strong,
        document.createTextNode(after)
      );
      this.timeEl = strong;
      this.hidden = false;
    }
    this.timeEl.textContent = this.formatDuration(remaining);
  }

  renderAfterCutoff(day) {
    if (!this.config.showAfterCutoff && !this.config.designMode) {
      this.state = 'hidden';
      this.hidden = true;
      return;
    }

    // Next dispatch day (0 = Mon): Mon-Thu evening -> next day,
    // Fri evening / Sat -> Monday, Sun -> Monday (which is "tomorrow").
    const next = day <= 3 ? day + 1 : 0;
    const isTomorrow = next === (day + 1) % 7;
    const stateKey = isTomorrow ? 'tomorrow' : `day-${next}`;
    if (this.state === stateKey) return;
    this.state = stateKey;
    this.timeEl = null;

    if (isTomorrow) {
  this.messageEl.textContent = this.config.strings.shipsTomorrow;
  this.hidden = false;
} else if (this.config.showShipsOnDay) {
  const dayName = new Intl.DateTimeFormat(this.config.locale, {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(new Date(WEEKDAY_REFERENCE + next * MS_PER_DAY));
  this.messageEl.textContent = this.config.strings.shipsOnDay.replace('[[day]]', dayName);
  this.hidden = false;
} else {
  this.hidden = true;
}
  }

  formatDuration(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const strings = this.config.strings;

    if (hours > 0) {
      return strings.durationHm.replace('[[h]]', hours).replace('[[m]]', minutes);
    }
    if (minutes > 0) {
      return strings.durationMs.replace('[[m]]', minutes).replace('[[s]]', seconds);
    }
    return strings.durationS.replace('[[s]]', seconds);
  }
}

if (!customElements.get('shipping-countdown')) {
  customElements.define('shipping-countdown', ShippingCountdown);
}
